/**
 * Starile de plata, pentru interfata.
 *
 * Codurile sunt cele CANONICE din `@evomag/core/status` (`approved`, `blocked`, ...), aceleasi pe
 * care le are `payment_status.code` in baza de date si pe care le trimite API-ul. Aici se adauga
 * doar stratul de prezentare: eticheta romneasca, litera de scurtatura si numele culorii din
 * Excel, pastrat ca punte de memorie pentru colegele care vin din fisier.
 *
 * De ce NU un vocabular romanesc separat pe client: doua seturi de coduri pentru aceeasi noțiune
 * ar diverge la prima stare adaugata, iar traducerea ar trebui facuta la fiecare granita.
 *
 * Culorile nu sunt aici: stau in `tokens.css` si se aplica prin `data-stare`. Un `switch` pe
 * culoare in TSX ar duplica sistemul de tokeni.
 */

import {
  STATUS,
  cycleStatus,
  statusFromNumericAlias,
  statusFromShortCode,
  type StatusCode,
} from '@evomag/core/status';

/** Codul canonic, identic cu `payment_status.code` din baza de date. */
export type CodStare = StatusCode;

/**
 * Starile din banda de legenda, in ordinea in care `Space` le cicleaza.
 *
 * `paid` s-a adaugat la cererea colegelor. Nu e din foaia `legenda` (acolo erau 6 culori), dar e
 * o stare pe care o folosesc zilnic si care pana acum exista doar ascunsa: tasta `P` o seta deja,
 * fiindca `statusFromShortCode` citeste tot catalogul, insa nu aparea nicaieri pe ecran.
 *
 * Declarat ca tuplu ne-vid, nu ca `string[]`, pentru ca `z.enum` din schema de search params are
 * nevoie de cel putin un element cunoscut la compilare.
 */
export const CODURI_STARE = [
  'approved',
  'blocked',
  'staged',
  'waiting',
  'verify',
  'urgent',
  'paid',
] as const satisfies readonly [StatusCode, ...StatusCode[]];

/** Doar starile care apar in legenda — cele care au glif si culoare proprie. */
export type CodStareLegenda = (typeof CODURI_STARE)[number];

export interface DefinitieStare {
  cod: CodStare;
  /** Eticheta romneasca, mereu afisata langa glif. */
  eticheta: string;
  /** Litera din grila compacta si scurtatura de tastatura. */
  litera: string;
  /** Alias numeric `1..6` pentru aceeasi actiune. */
  alias: string;
  /** Sensul din foaia `legenda`, folosit in legenda permanenta si in tooltip-uri. */
  descriere: string;
  /** Numele culorii originale din Excel, ca punte de memorie. */
  culoareExcel: string;
  /** Amanarea automata de la 18:00 nu atinge deciziile deliberate (blocat, esalonat). */
  amanareAutomata: boolean;
  /** Starile terminale nu intra in totalul zilei. */
  terminala: boolean;
}

/** ARGB-ul din Excel -> numele culorii, pentru textul de legenda. */
const NUME_CULOARE: Readonly<Record<string, string>> = {
  FF00FF00: 'verde',
  FFFF0000: 'roșu',
  FFFFFF00: 'galben',
  FF00FFFF: 'cyan',
  FFFF00FF: 'magenta',
  FFFF9900: 'portocaliu',
};

/** Descrierile afisate, mai explicite decat formularea din foaia `legenda`. */
const DESCRIERE: Readonly<Partial<Record<CodStare, string>>> = {
  approved: 'Plăți aprobate pentru ziua respectivă',
  blocked: 'Furnizor blocat — nu se plătește',
  staged: 'Plată eșalonată, sume periodice convenite',
  waiting: 'În așteptare (aprobare, confirmare, disponibil)',
  verify: 'De verificat recepția (extern) sau soldul (intern)',
  urgent: 'Plată urgentă, ca să nu ne blocheze furnizorul',
  paid: 'Plata a fost executată — rămâne în totalul zilei, banii au plecat azi',
};

function defineste(cod: CodStare): DefinitieStare {
  const meta = STATUS[cod];
  return {
    cod,
    eticheta: ETICHETA[cod] ?? meta.labelRo,
    litera: meta.shortCode,
    alias: meta.numericAlias === null ? '' : String(meta.numericAlias),
    descriere: DESCRIERE[cod] ?? meta.descriptionRo ?? '',
    culoareExcel: meta.argb ? (NUME_CULOARE[meta.argb] ?? '') : '',
    amanareAutomata: meta.autopostponeEligible,
    terminala: meta.isTerminal,
  };
}

/** Etichetele cu diacritice; `@evomag/core` le tine fara, ca sa fie sigure in SQL si in log-uri. */
const ETICHETA: Readonly<Partial<Record<CodStare, string>>> = {
  unset: 'Nemarcat',
  approved: 'Aprobat',
  blocked: 'Blocat',
  staged: 'Eșalonat',
  waiting: 'În așteptare',
  verify: 'De verificat',
  urgent: 'Urgent',
  paid: 'Plătit',
  postponed: 'Amânat',
  cancelled: 'Anulat',
};

export const STARI: readonly DefinitieStare[] = CODURI_STARE.map(defineste);

/** `A`/`a`/`1` => `approved`. Folosit de scurtaturile din grila. */
export function stareDupaTasta(tasta: string): CodStare | null {
  const cifra = Number.parseInt(tasta, 10);
  if (Number.isInteger(cifra)) return statusFromNumericAlias(cifra);
  return statusFromShortCode(tasta);
}

/** `Space` cicleaza in ordinea din legenda; din `nesetat` intra pe prima stare. */
export function urmatoareaStare(curenta: CodStare | null): CodStare {
  return cycleStatus(curenta ?? 'unset');
}

export function definitieStare(cod: CodStare): DefinitieStare {
  return defineste(cod);
}

/** Eticheta oricarei stari, inclusiv a celor terminale care nu apar in legenda. */
export function etichetaStare(cod: CodStare): string {
  return ETICHETA[cod] ?? STATUS[cod].labelRo;
}
